/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Messaging Service - Handles bidirectional messaging between coaches, students, and guardians
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import api from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('messagingService')

class MessagingService {
  // ===== CONVERSATION METHODS =====

  /**
   * Get all conversations for the current user
   * @param {Object} options
   * @param {number} [options.limit=20] - Number of conversations to fetch
   * @param {number} [options.offset=0] - Offset for pagination
   * @param {string} [options.status='active'] - Filter by status
   * @returns {Promise<Object>} Conversations list with pagination
   */
  async getConversations({ limit = 20, offset = 0, status = 'active' } = {}) {
    try {
      const response = await api.get('/messages/conversations', {
        params: { limit, offset, status }
      })
      return response.data
    } catch (error) {
      logger.error('Error fetching conversations:', error)
      throw error
    }
  }

  /**
   * Get a single conversation with messages
   * @param {string} conversationId - Conversation ID
   * @param {Object} options
   * @param {number} [options.messageLimit=50] - Number of messages to fetch
   * @param {number} [options.messageOffset=0] - Offset for message pagination
   * @returns {Promise<Object>} Conversation with messages
   */
  async getConversation(conversationId, { messageLimit = 50, messageOffset = 0 } = {}) {
    try {
      const response = await api.get(`/messages/conversations/${conversationId}`, {
        params: { messageLimit, messageOffset }
      })
      return response.data
    } catch (error) {
      logger.error('Error fetching conversation:', error)
      throw error
    }
  }

  /**
   * Start a new conversation
   * @param {Object} data
   * @param {string} data.coachId - Coach user ID (when student/guardian initiates)
   * @param {string} [data.participantId] - Participant ID (when coach initiates)
   * @param {string} [data.participantType] - 'student' or 'guardian'
   * @param {string} [data.regardingChildId] - Child ID (for guardian conversations)
   * @param {string} [data.subject] - Optional subject
   * @param {string} [data.initialMessage] - First message content
   * @returns {Promise<Object>} Created conversation
   */
  async startConversation(data) {
    try {
      const response = await api.post('/messages/conversations', data)
      return response.data
    } catch (error) {
      logger.error('Error starting conversation:', error)
      throw error
    }
  }

  /**
   * Archive a conversation
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Updated conversation
   */
  async archiveConversation(conversationId) {
    try {
      const response = await api.patch(`/messages/conversations/${conversationId}`, {
        status: 'archived'
      })
      return response.data
    } catch (error) {
      logger.error('Error archiving conversation:', error)
      throw error
    }
  }

  // ===== MESSAGE METHODS =====

  /**
   * Send a message in a conversation
   * @param {string} conversationId - Conversation ID
   * @param {string} content - Message content
   * @param {string} [parentMessageId] - Optional parent message ID for threading
   * @returns {Promise<Object>} Created message
   */
  async sendMessage(conversationId, content, parentMessageId = null) {
    try {
      const response = await api.post(`/messages/conversations/${conversationId}/messages`, {
        content,
        parentMessageId
      })
      return response.data
    } catch (error) {
      logger.error('Error sending message:', error)
      throw error
    }
  }

  /**
   * Mark all messages in a conversation as read
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Result with count of marked messages
   */
  async markAsRead(conversationId) {
    try {
      const response = await api.post(`/messages/conversations/${conversationId}/mark-read`)
      return response.data
    } catch (error) {
      logger.error('Error marking messages as read:', error)
      throw error
    }
  }

  /**
   * Delete a message (soft delete)
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} Updated message
   */
  async deleteMessage(messageId) {
    try {
      const response = await api.delete(`/messages/${messageId}`)
      return response.data
    } catch (error) {
      logger.error('Error deleting message:', error)
      throw error
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Get total unread message count
   * @returns {Promise<Object>} Unread count
   */
  async getUnreadCount() {
    try {
      const response = await api.get('/messages/unread-count')
      return response.data
    } catch (error) {
      logger.error('Error fetching unread count:', error)
      throw error
    }
  }

  /**
   * Get coaches available for messaging (for students/guardians)
   * @param {string} [childId] - Child ID (for guardians)
   * @returns {Promise<Object>} List of available coaches
   */
  async getAvailableCoaches(childId = null) {
    try {
      const response = await api.get('/messages/available-coaches', {
        params: childId ? { childId } : {}
      })
      return response.data
    } catch (error) {
      logger.error('Error fetching available coaches:', error)
      throw error
    }
  }

  // ===== GUARDIAN-SPECIFIC METHODS =====

  /**
   * Get conversations about a specific child (for guardians)
   * @param {string} childId - Child user ID
   * @param {Object} options
   * @returns {Promise<Object>} Conversations list
   */
  async getChildConversations(childId, { limit = 20, offset = 0 } = {}) {
    try {
      const response = await api.get(`/family/children/${childId}/conversations`, {
        params: { limit, offset }
      })
      return response.data
    } catch (error) {
      logger.error('Error fetching child conversations:', error)
      throw error
    }
  }

  /**
   * Start a conversation with a coach about a child (for guardians)
   * @param {string} childId - Child user ID
   * @param {string} coachId - Coach user ID
   * @param {string} [message] - Initial message
   * @returns {Promise<Object>} Created conversation
   */
  async startChildConversation(childId, coachId, message = null) {
    try {
      const response = await api.post(`/family/children/${childId}/conversations`, {
        coachId,
        message
      })
      return response.data
    } catch (error) {
      logger.error('Error starting child conversation:', error)
      throw error
    }
  }
}

export default new MessagingService()
